const DB_NAME = "bo-device-key";
const STORE_NAME = "keys";

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE_NAME);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbPut(db: IDBDatabase, key: string, value: unknown): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function idbGet<T>(db: IDBDatabase, key: string): Promise<T | null> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(key);
    req.onsuccess = () => resolve((req.result as T) ?? null);
    req.onerror = () => reject(req.error);
  });
}

// Store under a hash of userId, not the raw UUID, so the IndexedDB record key isn't a plain,
// casually-readable user identifier when inspected via DevTools.
async function hashUserId(userId: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(userId));

  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export async function getStoredDeviceKey(userId: string): Promise<CryptoKeyPair | null> {
  const db = await openDb();
  const key = await hashUserId(userId);

  return idbGet<CryptoKeyPair>(db, key);
}

export async function generateAndStoreDeviceKey(userId: string): Promise<CryptoKeyPair> {
  const keyPair = (await crypto.subtle.generateKey({ name: "ECDSA", namedCurve: "P-256" }, false, ["sign", "verify"])) as CryptoKeyPair;
  const db = await openDb();
  const key = await hashUserId(userId);
  await idbPut(db, key, keyPair);

  return keyPair;
}

export async function exportDevicePublicKeyB64(publicKey: CryptoKey): Promise<string> {
  const spki = await crypto.subtle.exportKey("spki", publicKey);

  return btoa(String.fromCharCode(...new Uint8Array(spki)));
}

export async function signChallengeB64(privateKey: CryptoKey, challengeB64: string): Promise<string> {
  const challengeBytes = Uint8Array.from(atob(challengeB64), (c) => c.charCodeAt(0));
  const sig = await crypto.subtle.sign({ name: "ECDSA", hash: "SHA-256" }, privateKey, challengeBytes);

  return btoa(String.fromCharCode(...new Uint8Array(sig)));
}
